import React, { useState } from "react";
import { DRAFT_STATUS } from "../utils/triloChatConstants";

/**
 * TriloChatDraftCard
 *
 * A passage MW composed for the CSR, shown for the user's approval. The user
 * can accept it as-is or edit it first; the decision is kept in the page's
 * draft state (see hooks/useTriloChatDrafts.js) so it survives a reload and can
 * be picked up by the PDF export.
 *
 * Props:
 * - draft: { title, content } as returned by the API
 * - draftKey: "<message index>-<draft index>", the key into draftStates
 * - draftStates: Map of draftKey -> { status, content, title, wasEdited }
 * - setDraftStates: State setter for draftStates
 */
const TriloChatDraftCard = ({
  draft,
  draftKey,
  draftStates,
  setDraftStates,
}) => {
  const state = draftStates[draftKey] || {
    status: DRAFT_STATUS.pending,
    content: draft.content,
    title: draft.title,
  };
  const [editText, setEditText] = useState(draft.content);
  const isEditing = state.status === DRAFT_STATUS.editing;

  const set = (status, content) => {
    const newContent = content ?? state.content;
    setDraftStates(prev => ({
      ...prev,
      [draftKey]: {
        status,
        content: newContent,
        title: draft.title,
        wasEdited:
          status === DRAFT_STATUS.accepted &&
          content !== undefined &&
          content !== draft.content,
      },
    }));
  };

  return (
    <div
      className={`trilochat__draft-card trilochat__draft-card--${state.status}`}>
      <div className="trilochat__draft-card-header">
        <span>{draft.title}</span>
        {state.status === DRAFT_STATUS.accepted && (
          <span className="trilochat__draft-card-status trilochat__draft-card-status--accepted">
            Accepted
          </span>
        )}
      </div>

      {/* TODO: number verification disabled — needs refinement
      {draft._unverified_numbers && draft._unverified_numbers.length > 0 && (
        <div className="trilochat__draft-card-warning">
          <span>⚠</span>
          <span>
            The following numbers could not be verified against source data:{" "}
            <strong>{draft._unverified_numbers.join(", ")}</strong>. Check before using.
          </span>
        </div>
      )} */}

      {isEditing ? (
        <textarea
          className="trilochat__draft-card-editor"
          value={editText}
          onChange={e => setEditText(e.target.value)}
        />
      ) : (
        <div className="trilochat__draft-card-content">{state.content}</div>
      )}

      <div className="trilochat__draft-card-actions">
        {state.status === DRAFT_STATUS.pending && (
          <>
            <button
              className="trilochat__draft-card-btn trilochat__draft-card-btn--accept"
              onClick={() => set(DRAFT_STATUS.accepted)}>
              Accept
            </button>
            <button
              className="trilochat__draft-card-btn"
              onClick={() => {
                setEditText(draft.content);
                set(DRAFT_STATUS.editing);
              }}>
              Edit
            </button>
          </>
        )}
        {isEditing && (
          <>
            <button
              className="trilochat__draft-card-btn trilochat__draft-card-btn--save"
              onClick={() => set(DRAFT_STATUS.accepted, editText)}>
              Save & Accept
            </button>
            <button
              className="trilochat__draft-card-btn"
              onClick={() => set(DRAFT_STATUS.pending)}>
              Cancel
            </button>
          </>
        )}
        {state.status === DRAFT_STATUS.accepted && (
          <button
            className="trilochat__draft-card-btn"
            onClick={() => {
              setEditText(state.content);
              set(DRAFT_STATUS.editing);
            }}>
            Edit
          </button>
        )}
      </div>
    </div>
  );
};

export default TriloChatDraftCard;
